import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../services/api';

const ProductTour = ({ onComplete, onSkip }) => {
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [highlightedElement, setHighlightedElement] = useState(null);
  
  useEffect(() => {
    loadTourProgress();
  }, []);
  
  const loadTourProgress = async () => {
    try {
      // Check if tour already completed
      const progressRes = await api.get('/tour/progress');
      if (progressRes.data.completed) {
        setVisible(false);
        return;
      }
      
      // Load tour steps
      const stepsRes = await api.get('/tour/steps');
      setSteps(stepsRes.data.steps || []);
      setCurrentStep(progressRes.data.current_step || 0);
      setVisible(true);
      
    } catch (error) {
      console.error('Failed to load tour:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const highlightElement = useCallback((selector) => {
    if (!selector) return;
    
    // Remove previous highlight
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });
    
    // Add new highlight
    const element = document.querySelector(selector);
    if (element) {
      element.classList.add('tour-highlight');
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedElement(element);
    }
  }, []);
  
  useEffect(() => {
    if (steps[currentStep]) {
      highlightElement(steps[currentStep].target_element);
    }
  }, [currentStep, steps, highlightElement]);
  
  const handleNext = async () => {
    const step = steps[currentStep];
    if (!step) return;
    
    try {
      await api.post(`/tour/progress/step/${step.step_id}`);
      
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Tour complete
        setVisible(false);
        onComplete?.();
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSkip = async () => {
    try {
      await api.post('/tour/progress/skip');
      setVisible(false);
      onSkip?.();
    } catch (error) {
      console.error('Failed to skip tour:', error);
    }
  };
  
  if (loading || !visible || steps.length === 0) {
    return null;
  }
  
  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  
  // Calculate position based on step config - Keep tooltip in viewport
  const getTooltipPosition = () => {
    const tooltipWidth = 400;
    const tooltipHeight = 250;
    const margin = 20;
    
    if (step.position === 'center') {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001,
        maxWidth: `${tooltipWidth}px`
      };
    }
    
    if (!highlightedElement) {
      return {
        position: 'fixed',
        bottom: `${margin}px`,
        right: `${margin}px`,
        zIndex: 10001,
        maxWidth: `${tooltipWidth}px`
      };
    }
    
    const rect = highlightedElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const style = { position: 'fixed', zIndex: 10001, maxWidth: `${tooltipWidth}px` };
    
    // Calculate initial position
    let top, left, bottom, right;
    
    switch (step.position) {
      case 'top':
        bottom = viewportHeight - rect.top + margin;
        left = rect.left;
        break;
      case 'bottom':
        top = rect.bottom + margin;
        left = rect.left;
        break;
      case 'left':
        top = rect.top;
        right = viewportWidth - rect.left + margin;
        break;
      case 'right':
        top = rect.top;
        left = rect.right + margin;
        break;
      default:
        bottom = margin;
        right = margin;
    }
    
    // Adjust to keep tooltip in viewport
    if (left !== undefined) {
      // Check right edge
      if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }
      // Check left edge
      if (left < margin) {
        left = margin;
      }
      style.left = `${left}px`;
    }
    
    if (right !== undefined) {
      // Check if it goes off left edge
      if (right + tooltipWidth > viewportWidth - margin) {
        style.left = `${margin}px`;
        delete style.right;
      } else {
        style.right = `${right}px`;
      }
    }
    
    if (top !== undefined) {
      // Check bottom edge
      if (top + tooltipHeight > viewportHeight - margin) {
        top = viewportHeight - tooltipHeight - margin;
      }
      // Check top edge
      if (top < margin) {
        top = margin;
      }
      style.top = `${top}px`;
    }
    
    if (bottom !== undefined) {
      // Check if it goes off top edge
      if (bottom + tooltipHeight > viewportHeight - margin) {
        style.top = `${margin}px`;
        delete style.bottom;
      } else {
        style.bottom = `${bottom}px`;
      }
    }
    
    return style;
  };
  
  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[10000]"
        onClick={(e) => e.stopPropagation()}
      />
      
      {/* Tour Tooltip */}
      <div 
        className="bg-white rounded-xl shadow-2xl p-6 max-w-md"
        style={getTooltipPosition()}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-slate-400 hover:text-slate-600 -mt-2 -mr-2"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Progress bar */}
        <div className="w-full h-1 bg-slate-200 rounded-full mb-4">
          <div 
            className="h-full bg-blue-600 rounded-full transition-all"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        
        {/* Content */}
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {step.title}
        </h3>
        <p className="text-slate-600 mb-6">
          {step.description}
        </p>
        
        {/* Actions */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="border-slate-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-slate-500"
            >
              Skip Tour
            </Button>
            <Button
              onClick={handleNext}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLastStep ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Finish
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      {/* CSS for highlighting */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 10001 !important;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.5), 0 0 0 8px rgba(99, 102, 241, 0.2) !important;
          border-radius: 8px !important;
        }
      `}</style>
    </>
  );
};

export default ProductTour;
